import type { OverviewChartRow, OverviewReportRow } from "@nexus/contracts";
import { config } from "@/config";
import { buildTopicDetailSearchFromReportRows } from "./buildTopicDetailSearch.js";
import { resolveTopicDetailSegment2 } from "./resolveTopicDetailSegment2.js";

export const TOPIC_DETAIL_SEGMENT1 = "Hash" as const;
export const TOPIC_DETAIL_SEGMENT3 = "All" as const;

/** Drawer + topic-detail API context for a single clicked overview topic. */
export interface TopicDetailContext {
  topicTitle: string;
  topicDescription: string;
  scenario: string;
  featureTimeFrame: string;
  /** Reco `search` param — AND-joined combinations for Examples/Fatigue BQ. */
  search: string;
  segment1: typeof TOPIC_DETAIL_SEGMENT1;
  segment2: string;
  segment3: typeof TOPIC_DETAIL_SEGMENT3;
}

export interface ResolveTopicDetailContextOptions {
  /** Summary rows — used to resolve combination/scenario for the clicked topic. */
  reportRows: OverviewReportRow[];
  /** Chart-visible topics — click is only valid when title is in this set. */
  chartRows: OverviewChartRow[];
  /** Raw Topic_Title from the chart category (not display-formatted). */
  topicTitle: string;
  /** v1: Nexus has no focus_entries; pass when available. */
  focusCombinations?: string[];
}

function pickPrimaryReportRow(rows: OverviewReportRow[]): OverviewReportRow | null {
  if (rows.length === 0) return null;
  return rows.reduce((best, row) => (row.SPEND > best.SPEND ? row : best), rows[0]!);
}

/**
 * Build topic-detail context from summary data. Uses the highest-SPEND row for
 * scenario/segment metadata and OR-joins all combinations for the clicked topic.
 */
export function resolveTopicDetailContext(
  options: ResolveTopicDetailContextOptions,
): TopicDetailContext | null {
  const { reportRows, chartRows, topicTitle, focusCombinations = [] } = options;

  const isOnChart = chartRows.some((r) => r.Topic_Title === topicTitle);
  if (!isOnChart) return null;

  const matchingRows = reportRows.filter((r) => r.Topic_Title === topicTitle);
  const primary = pickPrimaryReportRow(matchingRows);
  if (!primary) return null;

  if (config.isDev && matchingRows.length > 1) {
    console.warn(
      "[topic-detail] Multiple report rows for topic — using highest-SPEND row for scenario/segment; OR-joining all combinations for search",
      { topicTitle, rowCount: matchingRows.length, selectedSpend: primary.SPEND },
    );
  }

  const featureTimeFrame = primary.feature_time_frame;
  const scenario = primary.Scenario;

  return {
    topicTitle,
    topicDescription: primary.Topic_Description,
    scenario,
    featureTimeFrame,
    search: buildTopicDetailSearchFromReportRows(matchingRows, focusCombinations),
    segment1: TOPIC_DETAIL_SEGMENT1,
    segment2: resolveTopicDetailSegment2(scenario, featureTimeFrame),
    segment3: TOPIC_DETAIL_SEGMENT3,
  };
}

/** Drawer width — constant across tabs (Examples split layout needs 960px). */
export const TOPIC_DETAIL_DRAWER_WIDTH = "sm:max-w-[960px]";
